import { Box, FormControl, FormHelperText, Grid, InputLabel, OutlinedInput, InputAdornment, Stack, Typography, SelectChangeEvent, Select, MenuItem } from '@mui/material'
import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment, { Moment } from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import TransactionService from '../../../../../shared/services/transaction/transaction.service';
import { ReportPeriodContext } from '../../../../../context/report-periods/report-periods';
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications';
import { FileUploadHelper } from '../../../miscellaneous-components/file-upload-helper';
import { DialogSaveButtons } from '../../../miscellaneous-components/dialog-save-buttons';
import CustomFieldService from '../../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../../shared/services/custom-field/custom-field.model';
import { EmissionsInputAdvanced } from './emissions-input-advanced-form';
import { formatTransaction } from '../../../../../shared/utils/format-post-utils';
import { UserDetailsContext } from '../../../../../context/user-details/user-details';
import { ISubsidiary } from '../../../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../../../shared/utils/subsidiary-utils';
import { useIsReportPeriodLocked } from '../../../../../hooks/use-is-report-period-locked';
import { ScreenLabelsContext } from '../../../../../context/screen-labels/screen-labels'
import EmissionCalculationService, { ISupersedeDecision, SupersedeResolver } from '../../../../../shared/services/emission-calculation/emission-calculation.service';
import { IEmission } from '../../../../../shared/services/emission/emission.model'
import { IEmissionCalculation } from '../../../../../shared/services/emission-calculation/emission-calculation.model';
import { isEtsGroupId } from '../../../../../shared/utils/group-ids-utils';
import { resolveEmissionFieldFlags } from '../../../../../shared/utils/emission-field-config';

export const EmissionsInputDirect: FunctionComponent<{
  categoryId: string,
  groupId: string,
  onSave: (emissionCalculationIds?: string[]) => void,
  subsidiaries: ISubsidiary[],
  selectedSubsidiaryId: string,
  emission?: IEmission | null,
  editCalculation?: IEmissionCalculation | null,
  editCalculationIds?: string[],
  cancelHandler?: () => void,
  resolveSupersede?: SupersedeResolver,
}> = ({
  categoryId,
  groupId,
  onSave,
  subsidiaries,
  selectedSubsidiaryId,
  emission,
  editCalculation,
  editCalculationIds,
  cancelHandler,
  resolveSupersede,
}) => {
    //Services
    const transactionService = new TransactionService()
    const emissionCalculationService = new EmissionCalculationService()
    const navigate = useNavigate();
    const reportPeriodContext = useContext(ReportPeriodContext);
    const userNotificationsContext = useContext(UserNotificationsContext)
    const customFieldService = new CustomFieldService()
    const userDetailsContext = useContext(UserDetailsContext)
    const userDetails = userDetailsContext.userDetails
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const fieldFlags = useMemo(
      () => resolveEmissionFieldFlags(userDetails?.organisation?.emissionFieldConfig, customFields ?? []),
      [userDetails?.organisation?.emissionFieldConfig, customFields],
    );
    const [fileData, setFileData] = useState([]);
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //Loading State
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    //Use Effects
    useEffect(() => {
      formik.setFieldValue('categoryId', categoryId)
    }, [categoryId]);

    useEffect(() => {
      formik.setFieldValue('groupId', groupId)
    }, [groupId]);

    useEffect(() => {
      customFieldService.getAllActive(`&entryType=transaction`)
        .then(response => {
          setCustomFields(response);
          if (!emission) {
            response.forEach(cf => { formik.setFieldValue(cf.id, ''); });
          }
        })
        .catch(err => { console.log(err); })
    }, [])

    //Formik
    const req = (key: string, label: string) =>
      !emission && fieldFlags.mandatoryFields.includes(key)
        ? Yup.string().required(`${label} is required`)
        : Yup.string().nullable();

    // On an existing record the contextual fields (project, location, custom fields, exclusion
    // reason) are not shown, so they must not be required either: a required rule on a field
    // that is not rendered fails validation silently and the save never leaves the browser.
    const customFieldShape: Record<string, any> = {};
    (customFields ?? []).forEach(cf => {
      customFieldShape[cf.id] = !emission && fieldFlags.mandatoryFields.includes(cf.id)
        ? Yup.string().required(`${cf.fieldName} is required`)
        : Yup.string().nullable();
    });

    const transactionSchema = useMemo(() => Yup.object().shape({
      subsidiaryId: emission ? Yup.string().nullable() : Yup.string().required(`Please select a ${screenLabelsContext.getLabel('subsidiary', { plural: false })}`),
      countryId: emission ? Yup.string().nullable() : Yup.string().required(),
      groupId: Yup.string().required(),
      categoryId: Yup.string().required(),
      unitsConsumed: Yup.number().min(0.01, 'The amount must be greater than 0').required('You must provide an amount'),
      energyConsumption: Yup.number().min(0.0001, 'The energy consumption must be greater than 0').nullable(),
      startDate: emission ? Yup.string().nullable() : Yup.string().required('Start date is required'),
      endDate: emission ? Yup.string().nullable() : Yup.string().required('End date is required'),
      partOfEts: Yup.boolean(),
      regulatedEmissionTradingSchemeId: Yup.string().when('partOfEts', {
        is: true,
        then: schema => schema.required('You need to select a regulated emission trading scheme'),
        otherwise: schema => schema.nullable().notRequired()
      }),
      projectId: req('project', 'Project'),
      locationId: req('location', 'Location'),
      contactSupplierId: req('supplier', 'Supplier'),
      vehicleId: req('vehicle', 'Vehicle'),
      capitalGoodId: req('product', 'Product / Capital Good'),
      excludeFromReporting: Yup.boolean(),
      excludeFromReportingReason: Yup.string().when('excludeFromReporting', {
        is: (value: boolean) => value === true && !emission,
        then: (schema) => schema.required('You must provide a reason for exclusion'),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
      ...customFieldShape,
    }), [emission, screenLabelsContext, fieldFlags, customFields]);

    const formik = useFormik<any>({
      initialValues: {
        subsidiaryId: emission?.subsidiaryId ?? null,
        groupId: groupId,
        categoryId: categoryId,
        unitsConsumed: editCalculation?.unitsConsumed ?? null,
        energyConsumption: editCalculation?.transactionEnergyBreakdowns?.[0]?.kwhAmount ?? null,
        reportPeriodId: '',
        endDate: emission?.endDate ?? '',
        startDate: emission?.startDate ?? '',
        partOfEts: false,
        regulatedEmissionTradingSchemeId: null,
        locationId: null,
        projectId: null,
        contactSupplierId: null,
        vehicleId: null,
        capitalGoodId: emission?.capitalGoodId ?? null,
        countryId: emission?.countryId ?? null,
        excludeFromReporting: emission?.excludeFromReporting ?? false,
        excludeFromReportingReason: emission?.excludeFromReportingReason ?? '',
        isFlag: editCalculation?.isFlag === true,
        flagCategory: editCalculation?.flagCategory ?? '',
      },
      validationSchema: transactionSchema,
      onSubmit: async (values, { resetForm }) => {
        values.reportPeriodId = reportPeriodContext.lastSelectedReportId;
        if (isReportPeriodLocked) {
          userNotificationsContext.openDialog({
            active: true,
            title: 'Action Not Allowed',
            message: `The selected report period is locked and cannot be modified`,
            buttonCopy: 'Ok',
            hideCancelButton: true
          })
          return
        }
        if (isSubmitting) return
        setIsSubmitting(true)
        if (emission) {
          const createPayload = {
            groupId: groupId,
            categoryId: categoryId,
            unitsConsumed: values.unitsConsumed,
            energyConsumption: values.energyConsumption,
            type: 'DIRECT',
            isFlag: values.isFlag === true,
            flagCategory: values.flagCategory || undefined,
          }
          let savedMessage = 'Saved successfully'
          let supersede = false
          if (!editCalculationIds?.length && resolveSupersede) {
            let decision: ISupersedeDecision
            try {
              decision = await resolveSupersede()
            } catch {
              setIsSubmitting(false)
              return
            }
            supersede = decision.supersede
            savedMessage = decision.savedMessage
          }
          const calculationAction = editCalculationIds?.length
            ? emissionCalculationService.editEmissionCalculation(editCalculationIds, createPayload)
            : emissionCalculationService.createEmissionCalculation(emission.id, createPayload, supersede)
          calculationAction
            .then((response) => {
              resetForm()
              setIsSubmitting(false)
              onSave('emissionCalculationIds' in response ? response.emissionCalculationIds : undefined)
              userNotificationsContext.openSnackbar({ open: true, message: savedMessage })
            })
            .catch(err => {
              if (err.response?.data?.code === 1) {
                userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' })
              } else {
                const message = err?.response?.data?.message || 'There was an error, please try again'
                userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
              }
              console.log(err)
              setIsSubmitting(false)
            })
        } else {
          const response = formatTransaction(values, groupId, true, customFields)
          const form = new FormData()
          for (let i = 0; i < fileData.length; i++) {
            form.append('files', fileData[i])
          }
          form.append('transaction', JSON.stringify(response))
          transactionService.createDirectEntryTransaction(form)
            .then((response: any) => {
              resetForm()
              setIsSubmitting(false)
              // emissionId, never response.id: this endpoint returns the saved transaction, and the
              // detail route resolves an emission id. Sending the transaction id opened the page on
              // a record that does not exist.
              if (response?.emissionId) {
                navigate(`/emissions/overview/${response.emissionId}?backLink=/emissions/overview/group/${groupId}&backText=Back+to+group`);
              } else {
                onSave()
              }
            })
            .catch(err => {
              if (err.response?.data?.code === 1) {
                userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' })
              } else {
                const message = err?.response?.data?.message || 'There was an error, please try again'
                userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
              }
              console.log(err)
              setIsSubmitting(false)
            })
        }
      }
    })


    //Use Effects
    useEffect(() => {
      if (userDetails && subsidiaries) {
        const selectedSubsidiary = subsidiaries.find(s => s.id === selectedSubsidiaryId)
        if (selectedSubsidiary) {
          formik.setFieldValue('subsidiaryId', selectedSubsidiary.id)
          formik.setFieldValue('countryId', selectedSubsidiary.countryId)
        }
      }
    }, [userDetails])

    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setValues({ ...formik.values, [name]: date.format('YYYY-MM-DD') })
      }
    }

    // Validate before submitting so a failure on a field this form does not render (or a
    // rule that is wrong for this context) is reported rather than swallowed by Formik.
    const submitHandler = async () => {
      const errors = await formik.validateForm()
      const fields = Object.keys(errors)
      if (fields.length === 0) {
        await formik.submitForm()
        return
      }
      formik.setTouched(fields.reduce((acc, key) => ({ ...acc, [key]: true }), {}), false)
      const first = errors[fields[0]]
      const message = typeof first === 'string' && first ? first : 'Please check the highlighted fields'
      userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
    }

    const subsidiaryHandler = (e: SelectChangeEvent<string>) => {
      const subsidiary = subsidiaries.find(subsidiary => subsidiary.id === e.target.value)
      if (subsidiary) {
        formik.setFieldValue('subsidiaryId', subsidiary.id)
        formik.setFieldValue('countryId', subsidiary.countryId)
      }
    }

    return (
      <Box>
        <Box component='form' onSubmit={formik.handleSubmit}>
          {!emission && subsidiaries && (userDetails?.organisation?.enableSubsidiaries || subsidiaries.length > 1) &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='subsidiaryId'> {screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' })}</InputLabel>
              <Select
                id='subsidiaryId' label={screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' })} name='subsidiaryId'
                value={formik.values.subsidiaryId || ''}
                onChange={subsidiaryHandler}
                error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                sx={{ color: 'text.pprimary', }}>
                {getSelectableSubsidiaries(subsidiaries).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) =>
                  <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
                )}
              </Select>
              {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.subsidiaryId ? <div>{formik.errors.subsidiaryId as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {/* mt only: the field below brings its own mt: 2, and a mb here too opened a 32px gap
              where every other field pair on this form sits at 16px. */}
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='unitsConsumed'>Amount</InputLabel>
            <OutlinedInput
              inputProps={{ min: 0.01, step: 0.01 }}
              id='unitsConsumed'
              label='Amount'
              name='unitsConsumed'
              type='number'
              disabled={isSubmitting}
              value={formik.values.unitsConsumed ?? ''}
              onChange={formik.handleChange}
              error={formik.touched.unitsConsumed && Boolean(formik.errors.unitsConsumed)}
              endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.primary' }}>kg CO2e</Typography></InputAdornment>}
              sx={{ color: 'text.primary' }}></OutlinedInput>
            {formik.touched.unitsConsumed && formik.errors.unitsConsumed &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.unitsConsumed ? <div>{formik.errors.unitsConsumed as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>
          {(groupId === '22cc8b45-aa77-49aa-8fb2-c39bb884fa13' || groupId === '504daea8-fd6b-4b06-8e5f-dc1a90558ede' || groupId === 'acf9ae04-25d9-43d7-b442-fabd69e5844b') &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='energyConsumption'>Energy Consumption (Optional)</InputLabel>
              <OutlinedInput
                inputProps={{ min: 0.01, step: 0.01 }}
                id='energyConsumption'
                label='Energy Consumption (Optional)'
                name='energyConsumption'
                type='number'
                disabled={isSubmitting}
                value={formik.values.energyConsumption ?? ''}
                onChange={formik.handleChange}
                error={formik.touched.energyConsumption && Boolean(formik.errors.energyConsumption)}
                endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.primary' }}>kWh</Typography></InputAdornment>}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.energyConsumption && formik.errors.energyConsumption &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.energyConsumption ? <div>{formik.errors.energyConsumption as string}</div> : null}
                </FormHelperText>
              }
              <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1, fontSize: '0.8rem' }}>
                * If you do not provide this data, your SECR report will still include energy consumption figures, but they may not accurately represent your energy use
              </Typography>
            </FormControl>}
          {!emission && (
            <Grid container spacing={2} sx={{ mb: 2, pt: 2 }}>
              <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                <FormControl fullWidth variant='outlined'>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      minDate={moment(reportPeriodContext.getAllowedInputDates()?.startDate)}
                      maxDate={formik.values.endDate ? moment(formik.values.endDate) : moment(reportPeriodContext.getAllowedInputDates()?.endDate)}
                      label='Start Date'
                      value={formik.values.startDate ? moment(formik.values.startDate) : null}
                      onChange={(date) => dateHandler(date, 'startDate')}
                      format='DD/MM/YYYY'
                      sx={{ color: 'text.primary' }}
                      open={openStartDate}
                      onOpen={() => setOpenStartDate(true)}
                      onClose={() => setOpenStartDate(false)}
                      slotProps={{
                        textField: {
                          onClick: () => setOpenStartDate(true),
                          error: formik.touched.startDate && Boolean(formik.errors.startDate)
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {formik.touched.startDate && formik.errors.startDate &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.startDate ? <div>{formik.errors.startDate as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>
            </Grid>
            <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
              <FormControl fullWidth variant='outlined'>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    minDate={moment(formik.values.startDate)}
                    maxDate={moment(reportPeriodContext.getAllowedInputDates()?.endDate)}
                    label='End Date'
                    value={formik.values.endDate ? moment(formik.values.endDate) : null}
                    onChange={(date) => dateHandler(date, 'endDate')}
                    format='DD/MM/YYYY'
                    sx={{ color: 'text.primary' }}
                    open={openEndDate}
                    onOpen={() => setOpenEndDate(true)}
                    onClose={() => setOpenEndDate(false)}
                    slotProps={{
                      textField: {
                        onClick: () => setOpenEndDate(true),
                        error: formik.touched.endDate && Boolean(formik.errors.endDate)
                      },
                    }}
                  />
                </LocalizationProvider>
                {formik.touched.endDate && formik.errors.endDate &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.endDate ? <div>{formik.errors.endDate as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>
            </Grid>
          </Grid>
          )}
          {customFields && !emission &&
            <EmissionsInputAdvanced
              customFields={fieldFlags.visibleCustomFields}
              hideCountry={fieldFlags.hideCountry}
              hideLocation={fieldFlags.hideLocation}
              hideSupplier={fieldFlags.hideSupplier}
              hideVehicle={fieldFlags.hideVehicle}
              hideProduct={fieldFlags.hideProduct}
              hideProject={fieldFlags.hideProject}
              hideRegulatedEmissionTradingScheme={fieldFlags.hideEts || !isEtsGroupId(groupId)}
              hideExcludedFromReporting={fieldFlags.hideExcludeFromReporting}
              formik={formik}
              hideRec={true}
              hideEnergySupplierTariff={true}
              hideCustomBasedMarketMultiplier={true}
              mandatoryFields={fieldFlags.mandatoryFields}
            />}
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            {!emission && <FileUploadHelper fileData={fileData} setFileData={setFileData} disableButton={isSubmitting} />}
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={cancelHandler}
              hideCancelButton={!cancelHandler}
              submitHandler={submitHandler}
            />
          </Stack>
        </Box>
      </Box>
    )
  }